/**
 * ProtectedRoute component - Wraps routes that require authentication
 * UI gating uses portal auth state (authState); unauthenticated users go to /error, except during MSAL OAuth return.
 */
import React, { useRef, useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import {
  HSUS_SESSION_EXPIRY_LOCATION_STATE_KEY,
  hasPriorPortalAuthentication,
} from '@hsus-dev/session-policy';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { useAuth, AuthRedirectGuard } from '../../context/AuthContext';
import { LoadingSpinner } from '../Loading/LoadingSpinner';
import {
  LOADING_FORM_ARIA_LABEL,
  LOADING_FORM_MESSAGE,
  VERIFYING_ACCESS_ARIA_LABEL,
  VERIFYING_ACCESS_MESSAGE,
} from '../../config/loadingUi.config';
import { AUTH_API_CONFIG } from '../../config/api.config';
import { getAuthProvider } from '../../services/authApi.service';
import { MsalAuthProvider } from '../../services/msalAuthProvider.service';
import { getMsalInstance } from '../../services/msalBootstrap.service';
import { hasEmptyMsalAccountCache } from '../../utils/msalAccountPresence.utils';
import { isExamFormRoutePath } from '../../utils/examFormQuery.utils';

/** Auth / MSAL gates: verifying copy everywhere except `/examform`, which uses only “Loading form…”. */
function routeGateSpinner(examFormShell: boolean): React.ReactElement {
  const copy = examFormShell
    ? { message: LOADING_FORM_MESSAGE, ariaLabel: LOADING_FORM_ARIA_LABEL }
    : { message: VERIFYING_ACCESS_MESSAGE, ariaLabel: VERIFYING_ACCESS_ARIA_LABEL };
  return <LoadingSpinner {...copy} />;
}

/** True when the current URL looks like an MSAL redirect response (must not navigate away before handleRedirectPromise runs). */
function isBrowserMsalOAuthRedirect(): boolean {
  if (typeof window === 'undefined') return false;
  const urlParams = new URLSearchParams(window.location.search);
  const hashParams = new URLSearchParams(window.location.hash.substring(1));
  return (
    urlParams.has('code') ||
    urlParams.has('id_token') ||
    urlParams.has('access_token') ||
    hashParams.has('id_token') ||
    hashParams.has('access_token') ||
    urlParams.has('error') ||
    hashParams.has('error')
  );
}

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAuth?: boolean;
}

const ProtectedRouteComponent: React.FC<ProtectedRouteProps> = ({
  children,
  requireAuth = true,
}) => {
  const { authState, isLoading, isLoggingOut, isLoggingIn, user, refreshAuthState } = useAuth();
  const location = useLocation();
  const hasRedirectedRef = useRef<string | null>(null);

  // providerType is a build-time constant (VITE_AUTH_PROVIDER_TYPE); hook order is fixed per build.
  // MsalProvider is only mounted when providerType === 'msal' (see main.tsx).
  // eslint-disable-next-line react-hooks/rules-of-hooks -- MSAL hooks only safe under MsalProvider
  const msalResult = AUTH_API_CONFIG.providerType === 'msal' ? useMsal() : null;
  const { instance: msalInstance, inProgress } = msalResult || {
    instance: null,
    inProgress: InteractionStatus.None,
  };
  // eslint-disable-next-line react-hooks/rules-of-hooks -- MSAL hooks only safe under MsalProvider
  const _msalIsAuthenticated = AUTH_API_CONFIG.providerType === 'msal' ? useIsAuthenticated() : false;
  void _msalIsAuthenticated;

  const isAuthenticatedForRouting = authState === 'portal_authenticated' && !!user;
  const isAuthenticated = isAuthenticatedForRouting;

  const examFormLoadingShell = isExamFormRoutePath(location.pathname);

  const [hasAttemptedSilentAuth, setHasAttemptedSilentAuth] = useState(false);
  const silentAuthAttemptRef = useRef<string | null>(null);

  useEffect(() => {
    const attemptSilentAuth = async () => {
      if (
        !requireAuth ||
        isAuthenticated ||
        AUTH_API_CONFIG.providerType !== 'msal' ||
        !msalInstance ||
        inProgress !== InteractionStatus.None ||
        silentAuthAttemptRef.current === location.pathname
      ) {
        return;
      }

      silentAuthAttemptRef.current = location.pathname;
      setHasAttemptedSilentAuth(true);

      try {
        const provider = getAuthProvider();
        if (!(provider instanceof MsalAuthProvider)) {
          if (import.meta.env.DEV) {
            console.warn('[ProtectedRoute] Auth provider is not MSAL provider');
          }
          return;
        }

        if (import.meta.env.DEV) {
          console.log('[ProtectedRoute] Attempting silent authentication via Azure AD SSO...');
        }

        const token = await provider.getAccessTokenSilently();

        if (token) {
          try {
            await refreshAuthState();
            if (import.meta.env.DEV) {
              console.log('[ProtectedRoute] Silent authentication successful via Azure AD SSO');
            }
          } catch (refreshError) {
            console.warn('[ProtectedRoute] Failed to refresh AuthContext after silent auth:', refreshError);
          }
        }
      } catch (error) {
        if (error instanceof Error) {
          const errorMessage = error.message.toLowerCase();
          if (
            !errorMessage.includes('interaction_required') &&
            !errorMessage.includes('consent_required') &&
            !errorMessage.includes('login_required')
          ) {
            if (import.meta.env.DEV) {
              console.warn('[ProtectedRoute] Silent authentication failed:', error.message);
            }
          }
        }
        setTimeout(() => {
          if (silentAuthAttemptRef.current === location.pathname) {
            silentAuthAttemptRef.current = null;
            setHasAttemptedSilentAuth(false);
          }
        }, 1000);
      }
    };

    void attemptSilentAuth();
  }, [requireAuth, isAuthenticated, msalInstance, inProgress, location.pathname, refreshAuthState]);

  useEffect(() => {
    if (isAuthenticated) {
      hasRedirectedRef.current = null;
    }

    if (silentAuthAttemptRef.current !== location.pathname) {
      silentAuthAttemptRef.current = null;
      setHasAttemptedSilentAuth(false);
    }
  }, [location.pathname, isAuthenticated]);

  if (isLoading) {
    return routeGateSpinner(examFormLoadingShell);
  }

  if (isLoggingOut) {
    return examFormLoadingShell ? (
      <LoadingSpinner message={LOADING_FORM_MESSAGE} ariaLabel={LOADING_FORM_ARIA_LABEL} />
    ) : (
      <LoadingSpinner message="Signing out..." ariaLabel="Signing out" />
    );
  }

  if (authState === 'portal_validating') {
    return routeGateSpinner(examFormLoadingShell);
  }

  if (requireAuth && !isAuthenticatedForRouting && !isLoggingOut) {
    // Keep URL intact so MsalRedirectHandler can process ?code= / hash tokens (do not send user to /error yet).
    if (AUTH_API_CONFIG.providerType === 'msal' && isBrowserMsalOAuthRedirect()) {
      return routeGateSpinner(examFormLoadingShell);
    }
    if (AUTH_API_CONFIG.providerType === 'msal') {
      if (inProgress !== InteractionStatus.None) {
        return routeGateSpinner(examFormLoadingShell);
      }
      if (!hasAttemptedSilentAuth) {
        return routeGateSpinner(examFormLoadingShell);
      }
      const currentPath = location.pathname;
      if (hasRedirectedRef.current !== currentPath) {
        hasRedirectedRef.current = currentPath;
      }
      const pca = getMsalInstance() ?? msalInstance;
      const emptyLocalMsalAccountCache =
        pca !== null &&
        hasEmptyMsalAccountCache(pca) &&
        hasPriorPortalAuthentication();
      if (emptyLocalMsalAccountCache) {
        return (
          <AuthRedirectGuard
            to="/session-expired"
            state={{ [HSUS_SESSION_EXPIRY_LOCATION_STATE_KEY]: 'idle' }}
            replace
          />
        );
      }
      return <AuthRedirectGuard to="/error" replace />;
    }

    const currentPath = location.pathname;
    if (hasRedirectedRef.current !== currentPath) {
      hasRedirectedRef.current = currentPath;
    }
    return <AuthRedirectGuard to="/error" replace />;
  }

  if (!requireAuth && !isLoggingIn) {
    const urlParams = new URLSearchParams(window.location.search);
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const isMsalCallback =
      urlParams.has('code') ||
      urlParams.has('id_token') ||
      urlParams.has('access_token') ||
      hashParams.has('id_token') ||
      hashParams.has('access_token') ||
      urlParams.has('error') ||
      hashParams.has('error');

    if (isMsalCallback) {
      return <>{children}</>;
    }

    if (isAuthenticatedForRouting) {
      if (location.pathname === '/error') {
        return <>{children}</>;
      }
      const fromLoc = location.state?.from as { pathname?: string; search?: string } | undefined;
      const from = fromLoc ? `${fromLoc.pathname || '/error'}${fromLoc.search || ''}` : '/error';
      return <Navigate to={from} replace />;
    }
  }

  return <>{children}</>;
};

export const ProtectedRoute = ProtectedRouteComponent;
